/**
 * Governança & FinOps - Visão Geral / Hub
 * CTAs para navegação interna + Quick Stats (PT-BR)
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Wrench, Shield, DollarSign,
  AlertTriangle, Server, ChevronRight, TrendingUp, Megaphone, Package, Users
} from 'lucide-react';
import { COLORS } from '../../components/governance/governanceTheme';
import { AZURE } from '../../services/api';
import R from '../../navigation/opsRoutes';
import { FEATURES } from '../../config/features';
import { useGovernanceLang } from '../../contexts/GovernanceLangContext';

const OVERVIEW_LABELS = {
  pt: {
    pageTitle: 'Governânça Arena Stats',
    pageSubtitle: 'Centro de comando para operações e estratégia da plataforma',
    stats: ['Uptime (24h)', 'Agentes Ativos', 'Budget Utilizado', 'Incidentes Ativos'],
    sections: [
      { category: 'Execução', items: [
        { title: 'Planner Estratégico', description: 'Priorização WSJF com rastreamento de budget' },
        { title: 'Builder', description: 'Geração de tarefas com prompts Copilot prontos' },
      ]},
      { category: 'TI / Operações', items: [
        { title: 'Agentes AI', description: '14 agentes monitorados - ativar/desativar, health' },
        { title: 'Incidentes', description: 'Erros recentes, logs, rastreamento de resolução' },
        { title: 'Control Tower', description: 'Métricas em tempo real e ações rápidas' },
      ]},
      { category: 'Financeiro', items: [
        { title: 'Análise de Custos', description: 'Análise de custos por agente' },
      ]},
      { category: 'Investidores & Captação', items: [
        { title: 'Investidores', description: 'Documentos estratégicos, financeiros e institucionais para captação, valuation e due diligence' },
      ]},
      { category: 'Governânça Interna', items: [
        { title: 'RH & Pessoas', description: 'Controle de equipe, pró-labore, permissões internas e planejamento de headcount integrado ao financeiro.', cta: 'Acessar RH' },
      ]},
      { category: 'Go-to-market', items: [
        { title: 'Hub de Marketing', description: 'Conteúdo, analytics, creator e distribuição em um ponto único' },
        { title: 'Comercial — Planos', description: 'Planos, pricing e estratégia comercial padronizada' },
      ]},
    ],
    budgetLabel: (pct) => `Budget utilização: ${pct}%`,
    costsDesc: 'Análise de custos por agente',
    agentsActive: (n) => `${n} Ativos`,
  },
  en: {
    pageTitle: 'Arena Stats Governance',
    pageSubtitle: 'Command center for platform operations and strategy',
    stats: ['Uptime (24h)', 'Active Agents', 'Budget Used', 'Active Incidents'],
    sections: [
      { category: 'Execution', items: [
        { title: 'Strategic Planner', description: 'WSJF prioritization with budget tracking' },
        { title: 'Builder', description: 'Task generation with ready-made Copilot prompts' },
      ]},
      { category: 'IT / Operations', items: [
        { title: 'AI Agents', description: '14 monitored agents - enable/disable, health' },
        { title: 'Incidents', description: 'Recent errors, logs, resolution tracking' },
        { title: 'Control Tower', description: 'Real-time metrics and quick actions' },
      ]},
      { category: 'Financial', items: [
        { title: 'Cost Analysis', description: 'Cost analysis by agent' },
      ]},
      { category: 'Investors & Fundraising', items: [
        { title: 'Investors', description: 'Strategic, financial and institutional documents for fundraising, valuation and due diligence' },
      ]},
      { category: 'Internal Governance', items: [
        { title: 'HR & People', description: 'Team management, compensation, internal permissions and headcount planning integrated with financials.', cta: 'Go to HR' },
      ]},
      { category: 'Go-to-market', items: [
        { title: 'Marketing Hub', description: 'Content, analytics, creator and distribution in one place' },
        { title: 'Commercial — Plans', description: 'Plans, pricing and standardized commercial strategy' },
      ]},
    ],
    budgetLabel: (pct) => `Budget utilization: ${pct}%`,
    costsDesc: 'Cost analysis by agent',
    agentsActive: (n) => `${n} Active`,
  },
};

const GovernanceOverviewPage = () => {
  const [overview, setOverview] = useState(null);
  const { lang } = useGovernanceLang();
  const t = OVERVIEW_LABELS[lang] || OVERVIEW_LABELS.pt;

  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const { data } = await AZURE.get('/governance/overview');
        if (data?.success) setOverview(data.data);
      } catch (e) {
        console.error('[Governance] overview fetch failed:', e);
      }
    };
    fetchOverview();
  }, []);

  const SECTION_META = [
    { items: [{ to: R.execPlanner, icon: Target, bgColor: 'bg-gray-100', badge: 'WSJF' }, ...(FEATURES.GOV_BUILDER_ENABLED ? [{ to: R.execBuilder, icon: Wrench, bgColor: 'bg-purple-50' }] : [])] },
    { items: [
      { to: R.tiAgentes, icon: Shield, bgColor: 'bg-blue-50', badge: overview ? t.agentsActive(overview.total_agents ?? 0) : '...' },
      { to: R.tiIncidentes, icon: AlertTriangle, bgColor: 'bg-rose-50' },
      { to: R.tiControlTower, icon: Server, bgColor: 'bg-indigo-50' },
    ]},
    { items: [{ to: R.finCustos, icon: DollarSign, bgColor: 'bg-amber-50' }] },
    { items: [{ to: R.investidores, icon: TrendingUp, bgColor: 'bg-emerald-50', badge: 'Data Room' }] },
    { items: [{ to: R.rh, icon: Users, bgColor: 'bg-emerald-50' }] },
    { items: [{ to: R.marketingHub, icon: Megaphone, bgColor: 'bg-cyan-50' }, { to: R.comercialPlanos, icon: Package, bgColor: 'bg-lime-50' }] },
  ];

  const navigationCards = t.sections.map((sec, si) => ({
    category: sec.category,
    items: sec.items.map((item, ii) => ({
      ...item,
      ...SECTION_META[si].items[ii],
      description: si === 2 && overview
        ? t.budgetLabel(overview.budget_utilization_pct?.toFixed(0) ?? 0)
        : item.description,
    // Gate P1: itens sem meta (ex.: Builder removido do SECTION_META) não renderizam
    })).filter((item) => item.to),
  }));

  const quickStats = overview
    ? [
        { value: `${(100 - (overview.error_rate_pct || 0)).toFixed(1)}%`, label: t.stats[0], color: 'text-gray-700' },
        { value: `${overview.total_agents ?? 0}`, label: t.stats[1], color: overview.agents_unhealthy_count > 0 ? 'text-amber-600' : 'text-blue-600' },
        { value: `${(overview.budget_utilization_pct ?? 0).toFixed(0)}%`, label: t.stats[2], color: (overview.budget_utilization_pct ?? 0) > 85 ? 'text-rose-600' : 'text-amber-600' },
        { value: `${overview.incidents_count ?? 0}`, label: t.stats[3], color: (overview.incidents_count ?? 0) > 0 ? 'text-rose-600' : 'text-gray-700' },
      ]
    : t.stats.map(label => ({ value: '—', label, color: 'text-gray-400' }));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
            <h1 className="text-gray-900 text-2xl font-bold">
              {t.pageTitle}
            </h1>
            <p className="text-gray-600">
              {t.pageSubtitle}
            </p>
          </div>
        </div>
        
        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {quickStats.map((stat, idx) => (
            <div key={idx} className="bg-gray-50 rounded-lg p-4">
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Cards por Categoria */}
      {navigationCards.map((section) => (
        <div key={section.category}>
          <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 px-1">
            {section.category}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.items.map((card) => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.to}
                  to={card.to}
                  className="group bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md hover:border-gray-300 transition-all duration-200"
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 ${card.bgColor} rounded-lg group-hover:scale-105 transition-transform`}>
                      <Icon className="w-5 h-5" style={{ color: COLORS.primary }} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-base font-bold text-gray-900 group-hover:text-gray-800">
                          {card.title}
                        </h3>
                        {card.badge && (
                          <span className="px-2 py-0.5 bg-gray-100 text-xs font-semibold text-gray-600 rounded">
                            {card.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {card.description}
                      </p>
                      {card.cta && (
                        <div className="mt-3 inline-flex items-center px-3 py-1 rounded-md text-xs font-bold bg-gray-100 text-gray-700">
                          {card.cta}
                        </div>
                      )}
                    </div>
                    
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-700 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default GovernanceOverviewPage;
